import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled, {css, keyframes} from 'styled-components';
import {isEqual} from 'lodash';
import {Button, EMPHASIS, Alert} from '@forecast-it/design-system';
import {chatFont, D, skyTokens} from './design-tokens';
import {StatusPill} from './StatusPill';
import {ActionItemRow} from './ActionItemRow';
import {KeyValueTable} from './KeyValueTable';
import type {KeyValueRow} from './KeyValueTable';
import {ActionPhase} from './actionCardTypes';
import type {ActionPlan, ActionResult, AffectedItem, FieldDescriptor, HistoryProp} from './actionCardTypes';
import {validateField} from './fieldValidation';
import {PROJECT_ID_ALREADY_IN_USE_KEY} from './KeyValueForm';
import {EVENT_ID, dispatch} from '../../containers/event_manager';
import Util from '../../forecast-app/shared/util/util';
import ValidateProjectIdMutation from '../../mutations/ValidateProjectIdMutation';

const Card = styled.div<{$failed: boolean}>`
	border: 1px solid ${({$failed}) => ($failed ? D.v2.statusRed : D.assistantBubbleBorder)};
	border-radius: 8px;
	overflow: hidden;
	margin-top: 8px;
`;
const Body = styled.div`
	background: ${D.assistantBubble};
	padding: 16px;
`;
const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	gap: 8px;
`;
// sky/sys/font/heading/4 is byte-identical to the three declarations this used to
// hand-copy: ABC Whyte / 500 / 20px / 24px.
const HeaderTitle = styled.div`
	${skyTokens.font.heading['4']};
	color: ${skyTokens.color.text.content.main};
`;
const Summary = styled.p`
	margin: 0 0 12px;
	${skyTokens.font.body['2']};
	color: ${skyTokens.color.text.content.main};
`;
const Items = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;
const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 16px;
	align-items: center;
	padding: 12px 16px;
	background: ${D.bg};
	border-top: 1px solid ${D.assistantBubbleBorder};
`;
const dotPulse = keyframes`
	0%, 100% { opacity: 1; transform: scale(1); }
	50% { opacity: 0.35; transform: scale(0.65); }
`;

const ConfirmButtonContent = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 8px;
`;

const BlobbingDot = styled.span`
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;
	background: currentColor;
	${css`
		animation: ${dotPulse} 1.2s ease-in-out infinite;
	`}
`;

const BlockedButtonWrapper = styled.div`
	pointer-events: none;
	display: contents;
`;

// Separates the applied / not-applied breakdown from the affected rows above it.
const UnresolvedDetails = styled.div`
	margin-top: 12px;
`;

const CreateTooltip = styled.p`
	margin: 0;
	padding: 8px 12px;
	font-family: ${chatFont};
	font-size: 12px;
	color: ${skyTokens.color.text.content.auxiliary};
	font-style: italic;
`;

const seedEdits = (affected: AffectedItem[]): Record<string, Record<string, unknown>> =>
	Object.fromEntries(
		affected.map(item => [item.id, item.fields ? Object.fromEntries(item.fields.map(f => [f.key, f.value])) : {}])
	);

// Map a resolve-result status → terminal phase. Defined exhaustively so a
// malformed response (missing/unknown status) falls back to Failed rather than
// crashing on `status[0]` and wedging the card in the 'confirming' state.
const STATUS_TO_PHASE: Record<ActionResult['status'], ActionPhase> = {
	done: ActionPhase.Done,
	failed: ActionPhase.Failed,
	partial: ActionPhase.Partial,
	expired: ActionPhase.Expired,
	unresolved: ActionPhase.Unresolved,
};

export function ActionCard({
	plan,
	onRespond,
	onResume,
	onRegenerateQuery,
	paused = false,
	loading = false,
	resolved = false,
	result = null,
	dismissed: dismissedProp = false,
	history,
}: {
	plan: ActionPlan;
	onRespond: (decision: {
		approved: boolean;
		selectedIds: string[];
		overrides?: Record<string, Record<string, unknown>>;
	}) => Promise<ActionResult>;
	onResume?: () => void;
	onRegenerateQuery?: () => void;
	paused?: boolean;
	loading?: boolean;
	// Persisted resolution state (from the chat message). On remount / rehydrate
	// from sessionStorage these restore the card's terminal phase so a completed
	// action is NOT re-presented as a fresh, re-confirmable card — which would
	// double-execute the mutation server-side.
	resolved?: boolean;
	result?: ActionResult | null;
	dismissed?: boolean;
	history?: HistoryProp;
}): JSX.Element {
	const {formatMessage} = useIntl();
	// `plan_time_registration_create` does not support partial selection
	// (the create plan has no per-entity exclude semantic on MCP). The
	// gateway also rejects partial selection for this subtype, but the
	// FE pre-selects every row + hides the checkboxes so the user is never
	// presented with an affordance they can't use. (TNSR-247 2026-06-23.)
	const isCreate = plan.entity_subtype === 'create';
	// Single-entity cards have nothing to multi-select — auto-include the one item
	// and hide its checkbox so Confirm is enabled out of the box, matching the
	// Figma single-entity Action Box.
	const isSingle = plan.affected.length === 1;
	const [selectedIds, setSelectedIds] = useState<Set<string>>(() =>
		isCreate || isSingle ? new Set(plan.affected.map(a => a.id)) : new Set()
	);
	// Restore the terminal phase from a persisted result so a resolved card
	// rehydrated from sessionStorage shows Done/Partial/Unresolved/Failed/Expired
	// rather than a fresh, re-confirmable 'pending' card.
	const [phase, setPhase] = useState<ActionPhase>(() => {
		if (resolved && result) {
			if (result.status === 'done') return ActionPhase.Done;
			if (result.status === 'partial') return ActionPhase.Partial;
			if (result.status === 'expired') return ActionPhase.Expired;
			// Without this case a persisted unresolved result falls through to Failed,
			// which would claim nothing was applied and re-offer Retry on a write that
			// may already have landed.
			if (result.status === 'unresolved') return ActionPhase.Unresolved;
			return ActionPhase.Failed;
		}
		return loading ? ActionPhase.Thinking : ActionPhase.Pending;
	});
	const [dismissed, setDismissed] = useState(dismissedProp);
	const [lastResult, setLastResult] = useState<ActionResult | null>(result ?? null);
	// Re-entrancy latch: a synchronous double-click (or Retry fired before the
	// 'confirming' re-render disables the button) must not issue a second
	// resolve POST and double-execute the mutation.
	const inFlightRef = useRef(false);
	// Seed snapshot of the original field values, computed once. seedRef is the
	// immutable baseline for change-detection; edits starts as the same snapshot
	// (onEdit replaces objects immutably, so it never mutates the seed).
	const seedRef = useRef<Record<string, Record<string, unknown>>>(seedEdits(plan.affected));
	const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>(() => seedRef.current);
	// Live "already in use" check for the project_id field (mirrors the live
	// Create Project modal's own onCustomProjectIdBlur) — keyed by entity id
	// since at most one field on any one card triggers this. 'checking' and
	// 'taken' both block Confirm the same way a synchronous constraint
	// violation does; see hasFieldError below.
	const [projectIdCheck, setProjectIdCheck] = useState<Record<string, 'checking' | 'taken' | 'ok'>>({});
	const checkProjectId = (entityId: string, projectId: string): void => {
		if (!projectId) {
			setProjectIdCheck(prev => ({...prev, [entityId]: 'ok'}));
			return;
		}
		setProjectIdCheck(prev => ({...prev, [entityId]: 'checking'}));
		Util.CommitMutation(ValidateProjectIdMutation, {projectId}, (result: {validateProjectId: {valid: string}}) => {
			setProjectIdCheck(prev => ({...prev, [entityId]: result.validateProjectId.valid === 'true' ? 'ok' : 'taken'}));
		});
	};
	const onEdit = (entityId: string, key: string, value: unknown): void => {
		setEdits(prev => ({...prev, [entityId]: {...prev[entityId], [key]: value}}));
		if (key === 'project_id') checkProjectId(entityId, String(value ?? ''));
	};

	const isInFlightOrTerminal =
		phase === ActionPhase.Confirming ||
		phase === ActionPhase.Done ||
		phase === ActionPhase.Failed ||
		phase === ActionPhase.Partial ||
		phase === ActionPhase.Unresolved ||
		phase === ActionPhase.Expired;
	const effectivePhase: ActionPhase =
		paused && !isInFlightOrTerminal
			? ActionPhase.Paused
			: phase === ActionPhase.Pending && selectedIds.size > 0
			? ActionPhase.Selected
			: phase;
	const inFlight = phase === ActionPhase.Confirming;
	const isDone = phase === ActionPhase.Done;
	const isPartial = phase === ActionPhase.Partial;
	const isFailed = phase === ActionPhase.Failed;
	const isExpired = phase === ActionPhase.Expired;
	const isUnresolved = phase === ActionPhase.Unresolved;
	// Unresolved counts as terminal: the write left the building, so the card must
	// stop offering selection and re-confirmation even though the outcome is unknown.
	const isTerminal = isDone || isPartial || isUnresolved || isExpired;
	const boxesDisabled = loading || inFlight || isTerminal || paused;

	const toggle = (id: string): void => {
		if (isCreate || isSingle) return;
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const confirm = async (): Promise<void> => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		setPhase(ActionPhase.Confirming);
		let res: ActionResult;
		try {
			const overrides: Record<string, Record<string, unknown>> = {};
			for (const [entityId, entityEdits] of Object.entries(edits)) {
				// Only emit overrides for entities the user has selected.
				// A deselected entity (in exclude_ids on the server) must not
				// leak its edits into the overrides map.
				if (!selectedIds.has(entityId)) continue;
				const seed = seedRef.current[entityId] ?? {};
				const changed: Record<string, unknown> = {};
				for (const [k, v] of Object.entries(entityEdits)) {
					if (!isEqual(v, seed[k])) changed[k] = v;
				}
				if (Object.keys(changed).length > 0) overrides[entityId] = changed;
			}
			// Omit `overrides` entirely when no fields were changed (truly sparse).
			const hasOverrides = Object.keys(overrides).length > 0;
			res = await onRespond({
				approved: true,
				selectedIds: [...selectedIds],
				...(hasOverrides ? {overrides} : {}),
			});
		} catch (e) {
			// onRespond is expected to resolve (never reject) in production, but a
			// throwing transport must not wedge the card in the 'confirming' state.
			res = {status: 'failed', message: e instanceof Error ? e.message : undefined};
		} finally {
			inFlightRef.current = false;
		}
		setLastResult(res);
		setPhase(STATUS_TO_PHASE[res.status] ?? ActionPhase.Failed);
		// Once a task write settles, tell the workflow board (if mounted) to
		// highlight + scroll to the affected task(s) so they're easy to find among many.
		// Only on a fresh confirm() — the rehydrate path never runs here, so a restored
		// card doesn't re-highlight a stale action. `originalId` is the companyTaskId.
		if (plan.entity_type === 'task' && (res.status === 'done' || res.status === 'partial')) {
			const succeeded = res.succeededIds && res.succeededIds.length > 0 ? new Set(res.succeededIds) : null;
			const ids = plan.affected
				.filter(a => !succeeded || succeeded.has(a.id) || (a.originalId != null && succeeded.has(a.originalId)))
				.map(a => a.originalId ?? a.id);
			if (ids.length > 0) dispatch(EVENT_ID.HIGHLIGHT_TASKS, ids);
		}
		// A placeholder-create write executes out-of-band (server-side via MCP),
		// so the scheduling views — which fetch their own data via REST, not Relay —
		// never learn about it. If the user is on a schedule/timeline view, force a
		// data reload so the new placeholder (and its allocation) appear without a
		// manual refresh. Only on a fresh confirm() (the rehydrate path never runs
		// here). SCHEDULING_RELOAD_DATA re-fetches whichever project/view is mounted,
		// so no project-id match is needed.
		if (plan.entity_type === 'placeholder' && (res.status === 'done' || res.status === 'partial')) {
			if (Util.isScheduling()) dispatch(EVENT_ID.SCHEDULING_RELOAD_DATA);
		}
	};

	const cancel = (): void => {
		// Don't fire a reject while a confirm POST is already in flight — the
		// resolve is being processed, and a second (approved:false) POST would
		// race the in-flight approval for the same action. The button is also
		// disabled in this phase; the latch covers the synchronous double-click
		// before the disabling re-render lands.
		if (inFlightRef.current) return;
		void onRespond({approved: false, selectedIds: []});
		setDismissed(true);
	};

	const resume = (): void => {
		if (onResume) {
			onResume();
		}
	};

	if (dismissed) {
		return (
			<Summary data-testid="action-cancelled">
				{formatMessage({id: 'forecast_agent.action_card.cancelled', defaultMessage: 'Cancelled.'})}
			</Summary>
		);
	}

	const confirmEnabled = effectivePhase === ActionPhase.Selected || (isCreate && plan.affected.length > 0);
	const hasFieldError = plan.affected.some(item => {
		if (!selectedIds.has(item.id)) return false;
		if (!item.fields) return false;
		return item.fields.some((f: FieldDescriptor) => validateField(f, (edits[item.id] ?? {})[f.key]) !== null);
	});
	// A pending or failed project_id check blocks Confirm the same way a
	// synchronous constraint violation does — confirming while a check is
	// still in flight could submit a value the user is about to be told is
	// unavailable.
	const hasPendingOrFailedProjectIdCheck = plan.affected.some(
		item => selectedIds.has(item.id) && (projectIdCheck[item.id] === 'checking' || projectIdCheck[item.id] === 'taken')
	);
	const confirmEnabledFinal = confirmEnabled && !hasFieldError && !hasPendingOrFailedProjectIdCheck;
	const fieldOverrideErrorsFor = (itemId: string): Record<string, string> | undefined =>
		projectIdCheck[itemId] === 'taken' ? {project_id: PROJECT_ID_ALREADY_IN_USE_KEY} : undefined;

	// An unresolved outcome names the ids that DID apply and the per-entity failures.
	// Ids are matched against both `id` and `originalId` because the gateway returns
	// pre-dedup server ids, while `id` may carry a collision suffix.
	const titleForId = (rawId: string): string => {
		const item = plan.affected.find(a => a.id === rawId || a.originalId === rawId);
		return item ? item.title : rawId;
	};
	const unresolvedRows: KeyValueRow[] = [];
	if (isUnresolved) {
		const applied = lastResult?.succeededIds ?? [];
		if (applied.length > 0) {
			unresolvedRows.push({
				key: formatMessage({id: 'forecast_agent.action_card.unresolved_applied', defaultMessage: 'APPLIED'}),
				value: applied.map(titleForId).join(', '),
			});
		}
		const notApplied = lastResult?.failed ?? [];
		if (notApplied.length > 0) {
			unresolvedRows.push({
				key: formatMessage({id: 'forecast_agent.action_card.unresolved_not_applied', defaultMessage: 'NOT APPLIED'}),
				value: notApplied.map(f => `${titleForId(f.id)} — ${f.reason}`).join('; '),
			});
		}
	}
	// A failed outcome carries the same per-entity reasons, and its alert tells the
	// reader to review why each change was skipped — so the card must show them.
	if (isFailed) {
		const skipped = lastResult?.failed ?? [];
		if (skipped.length > 0) {
			unresolvedRows.push({
				key: formatMessage({id: 'forecast_agent.action_card.failed_skipped', defaultMessage: 'SKIPPED'}),
				value: skipped.map(f => `${titleForId(f.id)} — ${f.reason}`).join('; '),
			});
		}
	}
	// The verify handle arrives off the wire, so its tool name is guarded rather than
	// trusted — a non-string would render as "[object Object]" inside the prompt. The
	// handle's `how` text is deliberately not shown: it is prose written to instruct the
	// agent, not the user, so the card falls back to its own generic wording instead.
	const verifyTool =
		typeof lastResult?.verify?.tool === 'string' && lastResult.verify.tool.length > 0 ? lastResult.verify.tool : undefined;
	// The same handle carries `repeat_safe` for confirms whose write is idempotent, so
	// re-applying it changes nothing. The server sends the key only when that holds and
	// omits it otherwise, never sending false, which makes absence the cautious default.
	// The strict `=== true` matters twice over: the value is untrusted wire data, and a
	// truthy string such as "false" would otherwise flip the card into telling the user a
	// non-idempotent change is safe to run twice.
	const isRepeatSafe = lastResult?.verify?.repeat_safe === true;

	return (
		<Card $failed={isFailed}>
			<Body>
				<Header>
					<HeaderTitle>{plan.title}</HeaderTitle>
					<StatusPill phase={effectivePhase} />
				</Header>
				<Summary>{plan.summary}</Summary>
				{isCreate && !isTerminal && (
					<CreateTooltip data-testid="action-create-hint">
						{formatMessage({
							id: 'forecast_agent.action_card.create_no_partial',
							defaultMessage: 'New items can only be confirmed in full — partial selection is not available.',
						})}
					</CreateTooltip>
				)}
				<Items>
					{plan.affected.map(item => (
						<ActionItemRow
							key={item.id}
							item={item}
							checked={selectedIds.has(item.id)}
							disabled={boxesDisabled || isCreate}
							onToggle={toggle}
							showView={isDone || isPartial}
							history={history}
							entityType={plan.entity_type}
							edits={edits[item.id]}
							onEdit={onEdit}
							editsReadOnly={boxesDisabled || isTerminal}
							// Hide the checkbox for `create` (no per-entity selection), single-
							// entity cards (the one item is auto-included), and once the action
							// has settled — terminal states (Done/Partial/Expired) and Failed
							// show row results without a re-selection affordance.
							hideCheckbox={isCreate || isSingle || isTerminal || isFailed}
							fieldOverrideErrors={fieldOverrideErrorsFor(item.id)}
						/>
					))}
				</Items>
				{(isUnresolved || isFailed) && unresolvedRows.length > 0 && (
					<UnresolvedDetails data-testid="action-unresolved-details">
						<KeyValueTable rows={unresolvedRows} />
					</UnresolvedDetails>
				)}
			</Body>

			{/* Plan-time warnings, rendered before the outcome alerts so they read as
			    something to weigh before confirming rather than as a result. They stay
			    visible once the action settles: a warning like "this will not be visible
			    until the project setting is on" is still true after the write lands. */}
			{plan.warnings.map((warning, index) => (
				<Alert key={`${index}-${warning}`} type={Alert.TYPE.WARNING} text={warning} />
			))}
			{isFailed && (
				<Alert
					type={Alert.TYPE.ERROR}
					text={
						lastResult?.message ||
						formatMessage({
							id: 'forecast_agent.action_card.failed_message',
							defaultMessage: "That didn't work — the request timed out. Your data is unchanged.",
						})
					}
				/>
			)}
			{isPartial && (
				<Alert
					type={Alert.TYPE.WARNING}
					text={
						lastResult?.message ||
						formatMessage(
							{
								id: 'forecast_agent.action_card.partial_message',
								defaultMessage:
									'{succeededCount} item(s) updated; {failedCount} could not be updated. Your data for the failed items is unchanged.',
							},
							{
								succeededCount: lastResult?.succeededIds?.length ?? 0,
								failedCount: lastResult?.failed?.length ?? 0,
							}
						)
					}
				/>
			)}
			{isUnresolved && (
				<>
					<Alert
						type={Alert.TYPE.WARNING}
						text={
							lastResult?.message ||
							formatMessage({
								id: 'forecast_agent.action_card.unresolved_message',
								defaultMessage:
									"We couldn't confirm whether this change was saved. Part of it may already have been applied.",
							})
						}
					/>
					{/* Two axes decide this copy: whether the handle names a read tool, and whether
					    the write is idempotent. Naming the tool is always the better wording, which
					    is why the card writes its own string instead of reusing the server message
					    above. A repeat-safe write must not carry the double-application warning:
					    repeating it is the recovery action that works, and warning against it talks
					    the reader out of the one thing that fixes their problem. The server states
					    what happened and stops there — it deliberately says nothing about whether
					    to retry, because this alert is the only place that decides it. Do not add
					    that sentence back on the server side; it prints here already.

					    "Asking for this again", not "running this again": the footer below is
					    dropped for unresolved, so there is no Retry button on this card. The user's
					    route back is the chat. */}
					<Alert
						type={Alert.TYPE.WARNING}
						text={
							isRepeatSafe
								? verifyTool
									? formatMessage({
											id: 'forecast_agent.action_card.unresolved_verify_repeat_safe_tool',
											defaultMessage:
												'Ask me to look up the current state if you want to confirm what landed. Asking for this again is safe, and only affects what is still outstanding.',
									  })
									: formatMessage({
											id: 'forecast_agent.action_card.unresolved_verify_repeat_safe',
											defaultMessage:
												'Check the current state of the affected items if you want to confirm what landed. Asking for this again is safe, and only affects what is still outstanding.',
									  })
								: verifyTool
								? formatMessage({
										id: 'forecast_agent.action_card.unresolved_verify_tool',
										defaultMessage:
											'Ask me to look up the current state before trying this again — repeating it could apply the same change twice.',
								  })
								: formatMessage({
										id: 'forecast_agent.action_card.unresolved_verify',
										defaultMessage:
											'Check the current state of the affected items before trying this again — repeating it could apply the same change twice.',
								  })
						}
					/>
				</>
			)}
			{isExpired && (
				<Alert
					type={Alert.TYPE.WARNING}
					text={formatMessage({
						id: 'forecast_agent.action_card.expired_message',
						defaultMessage: 'Your session paused after 10 minutes of inactivity. Regenerate Reply to continue',
					})}
				/>
			)}

			{/* Unresolved drops the whole footer, like Done and Partial: Retry could apply an
			    already-landed write a second time, and Cancel would post a rejection for a
			    decision that has already been dispatched. */}
			{!isDone && !isPartial && !isUnresolved && (
				<Footer>
					<Button emphasis={EMPHASIS.LOW} disabled={inFlight} onPress={cancel}>
						{formatMessage({id: 'forecast_agent.action_card.cancel', defaultMessage: 'Cancel'})}
					</Button>
					{effectivePhase === ActionPhase.Paused ? (
						<Button emphasis={EMPHASIS.HIGH} onPress={resume}>
							{formatMessage({id: 'forecast_agent.action_card.resume', defaultMessage: 'Resume'})}
						</Button>
					) : isExpired ? (
						onRegenerateQuery ? (
							<Button emphasis={EMPHASIS.HIGH} icon={'reload'} onPress={onRegenerateQuery}>
								{formatMessage({
									id: 'forecast_agent.action_card.regenerate_query',
									defaultMessage: 'Regenerate Reply',
								})}
							</Button>
						) : null
					) : isFailed ? (
						<Button
							emphasis={EMPHASIS.HIGH}
							icon={'reload'}
							disabled={inFlight}
							waiting={inFlight}
							onPress={confirm}
						>
							{formatMessage({id: 'forecast_agent.action_card.retry', defaultMessage: 'Retry'})}
						</Button>
					) : inFlight ? (
						<BlockedButtonWrapper>
							<Button emphasis={EMPHASIS.HIGH} onPress={confirm}>
								<ConfirmButtonContent>
									<BlobbingDot />
									{formatMessage({
										id: 'forecast_agent.action_card.status.confirming',
										defaultMessage: 'Confirming',
									})}
								</ConfirmButtonContent>
							</Button>
						</BlockedButtonWrapper>
					) : (
						<Button emphasis={EMPHASIS.HIGH} icon={'checkCircle'} disabled={!confirmEnabledFinal} onPress={confirm}>
							{formatMessage({
								id: 'forecast_agent.action_card.confirm',
								defaultMessage: 'Confirm Action',
							})}
						</Button>
					)}
				</Footer>
			)}
		</Card>
	);
}
